import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { media } from "../../styles/responsive";
import { useAuthStore } from "../../store/useStore";

const SidebarContainer = styled.aside`
  width: 260px;
  height: 100vh;
  background: linear-gradient(135deg, #d2691e 0%, #b85a1a 100%);
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 20px rgba(210, 105, 30, 0.15);

  ${media.tablet`
    position: fixed;
    left: -260px;
    z-index: 100;
    transition: left 0.3s ease;
    
    &.open {
      left: 0;
    }
  `}
`;

const Logo = styled.div`
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);

  h1 {
    font-family: "Pretendard", sans-serif;
    font-weight: 700;
    font-size: 28px;
    color: #ffffff;
    letter-spacing: -0.5px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const MenuList = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 18px 24px;
  background-color: ${(props) =>
    props.$active ? "rgba(255, 255, 255, 0.15)" : "transparent"};
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background-color: ${(props) =>
      props.$active
        ? "rgba(255, 255, 255, 0.15)"
        : "rgba(255, 255, 255, 0.08)"};
    transform: translateX(4px);
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${(props) => (props.$active ? "#ffffff" : "transparent")};
    transition: all 0.3s ease;
    border-radius: 0 4px 4px 0;
  }

  span {
    font-family: "Pretendard", sans-serif;
    font-weight: ${(props) => (props.$active ? "600" : "400")};
    font-size: 16px;
    color: ${(props) => (props.$active ? "#ffffff" : "#f5f5f5")};
    transition: all 0.2s ease;
    text-shadow: ${(props) =>
      props.$active ? "0 1px 2px rgba(0, 0, 0, 0.1)" : "none"};
  }
`;

const AdminSection = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.1);
`;

const AdminInfo = styled.div`
  margin-bottom: 15px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const AdminName = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
`;

const AdminEmail = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
`;

const AdminMenuButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  &.danger {
    background: rgba(220, 53, 69, 0.2);
    border-color: rgba(220, 53, 69, 0.3);

    &:hover {
      background: rgba(220, 53, 69, 0.3);
    }
  }
`;

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout } = useAuthStore();

  const menuItems = [
    { path: "/dashboard", label: "메인" },
    { path: "/member", label: "회원관리" },
    { path: "/food-reviews", label: "음식 사진 검토" },
    { path: "/social-reports", label: "커뮤니티 신고 관리" },
    { path: "/app-releases", label: "앱 버전 관리" },
    { path: "/device-usage", label: "기기 사용 대시보드" },
    { path: "/health-reports", label: "회원 통합 리포트" },
    { path: "/faq", label: "FAQ" },
    { path: "/notice", label: "공지사항" },
  ];

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard" || location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    if (window.confirm("정말 로그아웃하시겠습니까?")) {
      logout();
      navigate("/login");
    }
  };

  const handleProfile = () => {
    navigate("/profile");
  };

  return (
    <SidebarContainer>
      <Logo>
        <h1>Lengsup</h1>
      </Logo>
      <MenuList>
        {menuItems.map((item) => (
          <MenuItem
            key={item.path}
            $active={isActive(item.path)}
            onClick={() => navigate(item.path)}
          >
            <span>{item.label}</span>
          </MenuItem>
        ))}
      </MenuList>

      {admin && (
        <AdminSection>
          <AdminInfo>
            <AdminName>{admin.name}</AdminName>
            <AdminEmail>{admin.email}</AdminEmail>
          </AdminInfo>

          <AdminMenuButton onClick={handleProfile}>프로필 수정</AdminMenuButton>

          <AdminMenuButton className="danger" onClick={handleLogout}>
            로그아웃
          </AdminMenuButton>
        </AdminSection>
      )}
    </SidebarContainer>
  );
};

export default Sidebar;
